import { ApolloError } from '@apollo/client';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { GraphQLError } from 'graphql';
import {
  AgencyActivityType,
  ChatClientTagType,
  ChatSubFlowName,
  ClientActivityType,
  ClientsImporterType,
  CrcStepShowEnum,
  DashboardPage,
  EmailDirectionEnum,
  EmailServiceEnum,
  EmailTemplateType,
  ExternalChatChannelType,
  FunnelStepActionType,
  GupshupTemplateCategory,
  GupshupTemplateStatus,
  GupshupTemplateType,
  ObservationSendingMethod,
  RealEstateExtraFeature,
  RealEstateType,
  RouletteStatus,
  UserRouletteEventType,
} from 'graphql/apollo-graphql-generated/globalTypes';
import { LoginMutation_login_user_agent_agencies_customAuxTagTexts } from 'graphql/User/apollo-graphql-generated/LoginMutation';

export const formatErrorMsg = (error: ApolloError | GraphQLError) => {
  if (!error) return '';
  return error.message.replace('GraphQL error:', '');
};

export const formatDocument = (document: string) => {
  if (!document || typeof document !== 'string') return '';
  const documentNumbers = document.replace(/[^0-9]/g, '');

  if (documentNumbers?.length <= 11) return formatCPF(documentNumbers);
  return formatCNPJ(documentNumbers);
}

export const formatCPF = (cpf: string) => {
  if (!cpf) return '';

  return cpf
    .replace(/[^0-9]/g, '') // keep only numbers
    .slice(0, 11)
    .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'); // insert dots and hiphen
};

export const formatCNPJ = (cnpj: string) => {
  if (!cnpj) return '';

  return cnpj
    .replace(/[^0-9]/g, '') // keep only numbers
    .slice(0, 14)
    .replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'); // insert dots and hiphen
};

/**
 * Translates system dealType string to user friendly string
 * @param deal system usageType or even already user friendly string
 */
export function prettifyDealType(deal: string) {
  if (deal === 'RENT' || deal === 'locacao' || deal === 'alugar' || deal === 'aluguel') {
    return 'Locação';
  } else if (deal === 'BUY' || deal === 'compra' || deal === 'comprar' || deal === 'vendas') {
    return 'Vendas';
  }
  if (deal === 'both' || deal === 'BOTH') return 'Vendas ou Locação';

  return deal;
}

/**
 * Translates system usageType string to user friendly string
 * @param usage system usageType string or even already user friendly string
 * @param useUsoWord if "Comercial" should be "Uso comercial" and so on
 */
export function prettifyUsage(usage: string, useUsoWord: boolean = true) {
  let useWord = useUsoWord ? 'Uso ' : '';

  if (['RESIDENTIAL', 'residencial', 'Residencial', 'particular'].includes(usage)) {
    return useWord + 'Residencial';
  } else if (['COMMERCIAL', 'comercial', 'comercio', 'Comercial'].includes(usage)) {
    return useWord + 'Comercial';
  } else if (usage === 'BOTH') {
    return 'Ambos';
  } else if (usage === 'CORPORATIVE') {
    return 'Corporativo';
  }

  return usage;
}

/**
 * Turns 'predio' into 'prédio', 'barracao' into 'barracão' etc
 * @returns string
 */
export const prettifyRealEstateType = (ret: RealEstateType | string, useCapStart?: boolean): string => {
  let resp: string = ret;

  if (ret === 'casaDeCondominio') resp = 'casa de condomínio';
  if (ret === 'predio') resp = 'prédio';
  if (ret === 'barracao') resp = 'barracão';
  if (ret === 'galpao') resp = 'galpão';
  if (ret === 'chacara') resp = 'chácara';
  if (ret === 'area') resp = 'área';
  if (ret === 'sitio') resp = 'sítio'

  return useCapStart ? capsStart(resp) : resp;
};

export function capsStart(str: string) {
  return str[0].toUpperCase() + str.substring(1).toLowerCase();
}

export function capsStartAllWords(str: string) {
  if (!str) return ''

  const words = str.split(/ +/)
  let res = ''
  for (const word of words) {
    if (!word || !word?.length || !word[0]) continue
    res += word[0].toUpperCase() + word.substring(1).toLowerCase() + ' ';
  }

  return res.trim()
}

export function truncate(str: string, n: number, htmlEllipsis?: boolean) {
  return str && str.length > n ? str.substr(0, n - 1) + (htmlEllipsis ? '&hellip;' : '...') : str;
}

export function formatDate(date: Date | string) {
  if (!date) return '-';
  return dayjs(date).format('DD/MM/YYYY');
}

export function formatDateShort(date: Date | string) {
  if (!date) return '-';
  return dayjs(date).format('DD/MM/YY');
}

export function formatDateHour(date: Date | string, showSeconds?: boolean) {
  if (!date) return '-';
  if (showSeconds) return dayjs(date).format('DD/MM/YY HH:mm:ss');
  return dayjs(date).format('DD/MM/YY HH:mm');
}

export function putSlashNinLongMessages(msg: string, limit: number) {
  const splited = msg.split(' ');
  let charCount = 0;
  let newSplit = [];

  for (let x = 0; x < splited.length; x++) {
    newSplit.push(splited[x]);
    charCount += splited[x].length;
    if (charCount > limit) {
      charCount = 0;
      newSplit.push('\n');
    }
  }
  return newSplit.join(' ');
}

export function asPercentage(num: number) {
  if (!num) return "-";
  return (num * 100).toFixed(1) + ' %';
}

export function siteNameFromUrl(str: string) {
  let splitRight = str.split('www.');
  if (splitRight.length < 2) {
    splitRight = str.split('//pr.');
    if (splitRight.length < 2) {
      splitRight = str.split('http://');
      if (splitRight.length < 2) {
        splitRight = str.split('https://');
        if (splitRight.length < 2) {
          return str;
        }
      }
    }
  }
  const splitMid = splitRight[1].split('.com');
  const newStr = splitMid.length > 1 ? capsStart(splitMid[0]) : str;
  const finalStr = newStr.split('imoveis')[0].split('vendas')[0].split('corretor')[0].split('imobiliaria')[0].split('locacao')[0];

  return finalStr;
}

export const prettifyUserRole = (role: string) => {
  if (role === 'FIREFLY_ADMIN') return 'Admin';
  if (role === 'HARRY_ADMIN') return 'Harry Admin';
  if (role === 'AGENT_MANAGER') return 'Gerente';
  if (role === 'AGENT_DIRECTOR') return 'Gestor';
  if (role === 'AGENT_CONSULTANT') return 'Consultor';
  if (role === 'AGENT_SALESMAN') return 'Corretor';
  if (role === 'AGENT_ATTENDANT') return 'Atendente';
  return role;
};

export const prettifyRegistrationChannel = (str: string) => {
  return str
    ?.replace('CHAT', 'Chat')
    ?.replace('API', 'Dashboard')
    ?.replace('EXTERNAL_EMAIL', 'E-mail Externo')
    ?.replace('FACEBOOK', 'Facebook')
    ?.replace('INSTAGRAM', 'Instagram')
    ?.replace('ZAP', 'Zap Imóveis')
    ?.replace('VIVA_REAL', 'Viva Real')
    ?.replace('OLX', 'OLX')
    ?.replace('IMO_CASA', 'Imo Casa')
    ?.replace('IMOVEL_WEB', 'Imóvel Web')
    ?.replace('CHAVES_NA_MAO', 'Chaves na Mão')
    ?.replace('APOLAR_WEBSITE', 'Site Apolar')
    ?.replace('APOLAR_AGENCY_WEBSITE', 'Filial Apolar')
    ?.replace('MERCADO_LIVRE', 'Mercado Livre')
    ?.replace('WHATSAPP', 'Whatsapp')
    ?.replace('C3DWEB', 'C3dweb')
    ?.replace('CLIQUEI_MUDEI', 'CliqueiMudei')
    ?.replace('RD_STATION', 'RD Station')
    ?.replace('FORM_SITE_TANTUS', 'Formulário Site Tantus')
    ?.replace('LAIS_IA', 'Lais Webhook')
};

export const prettifyFunnelStep = (str: string) => {
  return str
    ?.replace('DOCUMENTATION_ANALYSIS', 'Análise de documentação')
    ?.replace('DOCUMENTATION_DELIVERY', 'Entrega de documentação')
    ?.replace('FINANCIAL_PROPOSAL', 'Proposta financeira')
    ?.replace('IN_ATTENDANCE', 'Em atendimento')
    ?.replace('IN_NEGOTIATION', 'Em negociação')
    ?.replace('LEAD', 'Lead')
    ?.replace('NO_INTEREST', 'Sem Interesse no momento')
    ?.replace('NO_PROFILE', 'Cliente não tem perfil')
    ?.replace('PROPOSAL_ACCEPTED', 'Proposta aceita')
    ?.replace('RENTAL_PROFILE', 'Perfil para Locação')
    ?.replace('SALE_COMPLETED', 'Venda concluída')
    ?.replace('USED_PROFILE', 'Perfil para Usados');
};

export const prettifyCivilState = (str: string) => {
  return str
    ?.replace('DIVORCED', 'divorciado(a)')
    ?.replace('MARRIED', 'casado(a)')
    ?.replace('SEPARATED', 'separado(a)')
    ?.replace('SINGLE', 'solteiro(a)')
    ?.replace('UNMARRIED', 'desquitado(a)')
    ?.replace('WIDOWED', 'viúvo(a)');
};

/**
 * Expects any of ['MORNING', 'AFTERNOON', 'NIGHT', 'OTHER']
 */
export const prettifyPeriodOfDay = (str: string) => {
  if (str === 'MORNING') return 'Manhã';
  if (str === 'AFTERNOON') return 'Tarde';
  if (str === 'NIGHT') return 'Noite';
  if (str === 'OTHER') return 'Outros';

  return str;
};

export function prettifyWeekDay(weekDay: number): string {
  const weekDaysString = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
  return weekDaysString[weekDay];
}

/** prettify extrafeatures from real estate
 * @param feature RealEstateExtraFeature
 */
export default function prettifyExtraFeatures(feature: RealEstateExtraFeature) {
  if (feature === RealEstateExtraFeature.arCondicionado) return 'ar condicionado';
  if (feature === RealEstateExtraFeature.banheiroSocial) return 'banheiro social';
  if (feature === RealEstateExtraFeature.circuitoSeguranca) return 'circuito de segurança';
  if (feature === RealEstateExtraFeature.pisoCeramico) return 'piso cerâmico';
  if (feature === RealEstateExtraFeature.quadraEsportes) return 'quadra de esportes';
  if (feature === RealEstateExtraFeature.salaJantar) return 'sala de jantar';
  if (feature === RealEstateExtraFeature.salaoFestas) return 'salão de festas';
  return feature;
}

export const prettifyActivityType = (type: AgencyActivityType | ClientActivityType) => {
  if (!type) return null;

  switch (type) {
    // AgencyActivityType
    case 'CREATE_AGENCY':
      return 'Criação de agência';
    case 'CREATE_SALES_GROUP':
      return 'Criação de grupo de corretores';
    case 'UPDATE_AGENCY_DATA':
      return 'Alteração de agência';
    case 'UPDATE_SALES_GROUP':
      return 'Alteração de grupo de corretores';
    case 'CREATE_ROULETTE_PERIOD':
      return 'Criação de período customizado';
    case 'UPDATE_ROULETTE_PERIOD':
      return 'Alteração de período customizado';
    case 'CREATE_CHAT_WIDGET':
      return 'Criação de chat widget';
    case 'UPDATE_CHAT_WIDGET':
      return 'Alteração de chat widget';

    // ClientActivityType
    case 'ADDED_FROM_API':
      return 'Adicionado pelo Dashboard';
    case 'ADDED_FROM_EMAIL':
      return 'Adicionado por Email';
    case 'ADDED_FROM_WEBHOOK':
      return 'Adicionado por Webhook';
    case 'ADDED_FROM_CLIENT_IMPORTER':
      return 'Adicionar via Importador de clientes';
    case 'CHANGED_AGENCY':
      return 'Mudança de Agência';
    case 'CHANGED_CLIENT_DATA':
      return 'Mudança de dados';
    case 'CHANGED_CLIENT_FUNNEL_STEP':
      return 'Mudança de Etapa Funil';
    case 'CLONE_CREATED':
      return 'Clone criado';
    case 'CLONE_SYNCED':
      return 'Clone sincronizado';
    case 'CLONE_NOT_CREATED':
      return "Clone não criado";
    case 'CRM_FUNNEL_STEP_UPDATED':
      return 'Sincronização de etapa funil no CRM';
    case 'INSERTED_IN_CRM':
      return 'Inserido no CRM';
    case 'MANUAL_ACTION':
      return 'Ação manual';
    case 'RESTORED':
      return 'Restaurado';
    case 'SENT_OBSERVATION_MESSAGE':
      return 'Mensagem de observação enviada';
    case 'SENT_WHATSAPP_MESSAGE':
      return 'Mensagem Whatsapp enviada';
    case 'SET_AS_DELETED':
      return 'Marcado como deletado';
    case 'SHARED_WITH_USERS':
      return 'Compartilhado com usuários';
    case 'UNSHARED_WITH_USERS':
      return 'Descompartilhado com usuários';
    case 'UPDATE_TAGS':
      return 'Tags atualizadas';
    case 'ENDED_CHAT':
      return 'Atendimento encerrado';
    case 'UNIFIED':
      return 'Cliente unificado';

    default:
      return type;
  }
};

export const prettifyEmailDirection = (direction: EmailDirectionEnum) => {
  if (!direction) return null;

  switch (direction) {
    case 'RECEIVED':
      return 'Recebido';
    case 'SENT':
      return 'Enviado';
    default:
      return direction;
  }
};

export const prettifyEmailService = (service: EmailServiceEnum) => {
  if (!service) return null;

  switch (service) {
    case 'SENDGRID':
      return 'Sendgrid';
    case 'SES':
      return 'SES';
    default:
      return service;
  }
};

export const prettifyChatClientTagType = (tag: ChatClientTagType, customAuxTagTexts?: LoginMutation_login_user_agent_agencies_customAuxTagTexts[]) => {
  if (!tag) return '';
  let customText = undefined;

  switch (tag) {
    case 'AUX_1':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_1')?.text;
      return customText ? customText : 'Auxiliar 1';
    case 'AUX_2':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_2')?.text;
      return customText ? customText : 'Auxiliar 2';
    case 'AUX_3':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_3')?.text;
      return customText ? customText : 'Auxiliar 3';
    case 'AUX_4':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_4')?.text;
      return customText ? customText : 'Auxiliar 4';
    case 'AUX_5':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_5')?.text;
      return customText ? customText : 'Auxiliar 5';
    case 'AUX_6':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_6')?.text;
      return customText ? customText : 'Auxiliar 6';
    case 'AUX_7':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_7')?.text;
      return customText ? customText : 'Auxiliar 7';
    case 'AUX_8':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_8')?.text;
      return customText ? customText : 'Auxiliar 8';
    case 'AUX_9':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_9')?.text;
      return customText ? customText : 'Auxiliar 9';
    case 'AUX_10':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_10')?.text;
      return customText ? customText : 'Auxiliar 10';
    case 'AUX_11':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_11')?.text;
      return customText ? customText : 'Auxiliar 11';
    case 'AUX_12':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_12')?.text;
      return customText ? customText : 'Auxiliar 12';
    case 'AUX_13':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_13')?.text;
      return customText ? customText : 'Auxiliar 13';
    case 'AUX_14':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_14')?.text;
      return customText ? customText : 'Auxiliar 14';
    case 'AUX_15':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_15')?.text;
      return customText ? customText : 'Auxiliar 15';
    case 'AUX_16':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_16')?.text;
      return customText ? customText : 'Auxiliar 16';
    case 'AUX_17':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_17')?.text;
      return customText ? customText : 'Auxiliar 17';
    case 'AUX_18':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_18')?.text;
      return customText ? customText : 'Auxiliar 18';
    case 'AUX_19':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_19')?.text;
      return customText ? customText : 'Auxiliar 19';
    case 'AUX_20':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_20')?.text;
      return customText ? customText : 'Auxiliar 20';
    case 'AUX_21':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_21')?.text;
      return customText ? customText : 'Auxiliar 21';
    case 'AUX_22':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_22')?.text;
      return customText ? customText : 'Auxiliar 22';
    case 'AUX_23':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_23')?.text;
      return customText ? customText : 'Auxiliar 23';
    case 'AUX_24':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_24')?.text;
      return customText ? customText : 'Auxiliar 24';
    case 'AUX_25':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_25')?.text;
      return customText ? customText : 'Auxiliar 25';
    case 'AUX_26':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_26')?.text;
      return customText ? customText : 'Auxiliar 26';
    case 'AUX_27':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_27')?.text;
      return customText ? customText : 'Auxiliar 27';
    case 'AUX_28':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_28')?.text;
      return customText ? customText : 'Auxiliar 28';
    case 'AUX_29':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_29')?.text;
      return customText ? customText : 'Auxiliar 29';
    case 'AUX_30':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_30')?.text;
      return customText ? customText : 'Auxiliar 30';
    case 'AUX_31':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_31')?.text;
      return customText ? customText : 'Auxiliar 31';
    case 'AUX_32':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_32')?.text;
      return customText ? customText : 'Auxiliar 32';
    case 'AUX_33':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_33')?.text;
      return customText ? customText : 'Auxiliar 33';
    case 'AUX_34':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_34')?.text;
      return customText ? customText : 'Auxiliar 34';
    case 'AUX_35':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_35')?.text;
      return customText ? customText : 'Auxiliar 35';
    case 'AUX_36':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_36')?.text;
      return customText ? customText : 'Auxiliar 36';
    case 'AUX_37':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_37')?.text;
      return customText ? customText : 'Auxiliar 37';
    case 'AUX_38':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_38')?.text;
      return customText ? customText : 'Auxiliar 38';
    case 'AUX_39':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_39')?.text;
      return customText ? customText : 'Auxiliar 39';
    case 'AUX_40':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_40')?.text;
      return customText ? customText : 'Auxiliar 40';
    case 'AUX_41':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_41')?.text;
      return customText ? customText : 'Auxiliar 41';
    case 'AUX_42':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_42')?.text;
      return customText ? customText : 'Auxiliar 42';
    case 'AUX_43':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_43')?.text;
      return customText ? customText : 'Auxiliar 43';
    case 'AUX_44':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_44')?.text;
      return customText ? customText : 'Auxiliar 44';
    case 'AUX_45':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_45')?.text;
      return customText ? customText : 'Auxiliar 45';
    case 'AUX_46':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_46')?.text;
      return customText ? customText : 'Auxiliar 46';
    case 'AUX_47':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_47')?.text;
      return customText ? customText : 'Auxiliar 47';
    case 'AUX_48':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_48')?.text;
      return customText ? customText : 'Auxiliar 48';
    case 'AUX_49':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_49')?.text;
      return customText ? customText : 'Auxiliar 49';
    case 'AUX_50':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_50')?.text;
      return customText ? customText : 'Auxiliar 50';
    case 'AUX_51':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_51')?.text;
      return customText ? customText : 'Auxiliar 51';
    case 'AUX_52':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_52')?.text;
      return customText ? customText : 'Auxiliar 52';
    case 'AUX_53':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_53')?.text;
      return customText ? customText : 'Auxiliar 53';
    case 'AUX_54':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_54')?.text;
      return customText ? customText : 'Auxiliar 54';
    case 'AUX_55':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_55')?.text;
      return customText ? customText : 'Auxiliar 55';
    case 'AUX_56':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_56')?.text;
      return customText ? customText : 'Auxiliar 56';
    case 'AUX_57':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_57')?.text;
      return customText ? customText : 'Auxiliar 57';
    case 'AUX_58':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_58')?.text;
      return customText ? customText : 'Auxiliar 58';
    case 'AUX_59':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_59')?.text;
      return customText ? customText : 'Auxiliar 59';
    case 'AUX_60':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_60')?.text;
      return customText ? customText : 'Auxiliar 60';
    case 'AUX_61':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_61')?.text;
      return customText ? customText : 'Auxiliar 61';
    case 'AUX_62':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_62')?.text;
      return customText ? customText : 'Auxiliar 62';
    case 'AUX_63':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_63')?.text;
      return customText ? customText : 'Auxiliar 63';
    case 'AUX_64':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_64')?.text;
      return customText ? customText : 'Auxiliar 64';
    case 'AUX_65':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_65')?.text;
      return customText ? customText : 'Auxiliar 65';
    case 'AUX_66':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_66')?.text;
      return customText ? customText : 'Auxiliar 66';
    case 'AUX_67':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_67')?.text;
      return customText ? customText : 'Auxiliar 67';
    case 'AUX_68':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_68')?.text;
      return customText ? customText : 'Auxiliar 68';
    case 'AUX_69':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_69')?.text;
      return customText ? customText : 'Auxiliar 69';
    case 'AUX_70':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_70')?.text;
      return customText ? customText : 'Auxiliar 70';
    case 'AUX_71':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_71')?.text;
      return customText ? customText : 'Auxiliar 71';
    case 'AUX_72':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_72')?.text;
      return customText ? customText : 'Auxiliar 72';
    case 'AUX_73':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_73')?.text;
      return customText ? customText : 'Auxiliar 73';
    case 'AUX_74':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_74')?.text;
      return customText ? customText : 'Auxiliar 74';
    case 'AUX_75':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_75')?.text;
      return customText ? customText : 'Auxiliar 75';
    case 'AUX_76':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_76')?.text;
      return customText ? customText : 'Auxiliar 76';
    case 'AUX_77':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_77')?.text;
      return customText ? customText : 'Auxiliar 77';
    case 'AUX_78':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_78')?.text;
      return customText ? customText : 'Auxiliar 78';
    case 'AUX_79':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_79')?.text;
      return customText ? customText : 'Auxiliar 79';
    case 'AUX_80':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_80')?.text;
      return customText ? customText : 'Auxiliar 80';
    case 'AUX_81':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_81')?.text;
      return customText ? customText : 'Auxiliar 81';
    case 'AUX_82':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_82')?.text;
      return customText ? customText : 'Auxiliar 82';
    case 'AUX_83':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_83')?.text;
      return customText ? customText : 'Auxiliar 83';
    case 'AUX_84':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_84')?.text;
      return customText ? customText : 'Auxiliar 84';
    case 'AUX_85':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_85')?.text;
      return customText ? customText : 'Auxiliar 85';
    case 'AUX_86':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_86')?.text;
      return customText ? customText : 'Auxiliar 86';
    case 'AUX_87':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_87')?.text;
      return customText ? customText : 'Auxiliar 87';
    case 'AUX_88':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_88')?.text;
      return customText ? customText : 'Auxiliar 88';
    case 'AUX_89':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_89')?.text;
      return customText ? customText : 'Auxiliar 89';
    case 'AUX_90':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_90')?.text;
      return customText ? customText : 'Auxiliar 90';
    case 'AUX_91':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_91')?.text;
      return customText ? customText : 'Auxiliar 91';
    case 'AUX_92':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_92')?.text;
      return customText ? customText : 'Auxiliar 92';
    case 'AUX_93':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_93')?.text;
      return customText ? customText : 'Auxiliar 93';
    case 'AUX_94':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_94')?.text;
      return customText ? customText : 'Auxiliar 94';
    case 'AUX_95':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_95')?.text;
      return customText ? customText : 'Auxiliar 95';
    case 'AUX_96':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_96')?.text;
      return customText ? customText : 'Auxiliar 96';
    case 'AUX_97':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_97')?.text;
      return customText ? customText : 'Auxiliar 97';
    case 'AUX_98':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_98')?.text;
      return customText ? customText : 'Auxiliar 98';
    case 'AUX_99':
      customText = customAuxTagTexts?.find((cur) => cur.auxTag === 'AUX_99')?.text;
      return customText ? customText : 'Auxiliar 99';
    case 'ADMINISTRATIVE':
      return "Administrativo"
    case "ANGARIATION":
      return "Angariação"
    // case "API":
    //   return "Dashboard"
    case "APOLAR_AGENCY_WEBSITE":
      return 'Site Loja';
    case "APOLAR_WEBSITE":
      return 'Site Apolar';
    case "BILLET":
      return "Boleto"
    case "BUY":
      return "Vendas"
    case "C3DWEB":
      return 'C3DWEB';
    case "CHATBOT":
      return "Chatbot"
    case "CHAVES_NA_MAO":
      return 'Chaves na Mão';
    case "COMPLAIN":
      return "Reclamação"
    case "COMPLIMENT":
      return "Elogio"
    case "CRM":
      return "CRM"
    case "CRM_ERROR":
      return "CRM Erro"
    case "DASHBOARD":
      return "Dashboard"
    case "DOCUMENTING_QUESTION":
      return "Documentação"
    case "EMAIL":
      return "E-mail"
    case "FACEBOOK":
      return "Facebook"
    case "INSTAGRAM":
      return "Instagram"
    case "FINANCIAL":
      return "Financeiro"
    case "FINANCIAL_SIMULATION":
      return "Simulação Financeira"
    case "HAS_CLONES":
      return "Pai de clone"
    case "HAS_FILES":
      return "Arquivo(s)"
    case "HUMAN_RESOURCES":
      return 'Recursos Humanos';
    case "IMOVEL_WEB":
      return 'Imóvel Web';
    case "IMO_CASA":
      return 'Imo Casa';
    case "IS_CLONE":
      return "Clone"
    case "MAINTENANCE":
      return 'Manutenção';
    case "MERCADO_LIVRE":
      return 'Mercado Livre';
    case "MESSAGE":
      return "Mensagem"
    case "NAME_CALLING":
      return "Insulto"
    case "NOTIFICATION":
      return "Notificação"
    case "PHONE":
      return "Telefone"
    case "PURCHASES":
      return 'Compras';
    case "OBSERVATION":
      return 'Observação';
    case "OLX":
      return 'OLX';
    case "OPPORTUNITY":
      return 'Oportunidade';
    case "REAL_ESTATE_REFERENCE":
      return "Referência"
    case "RENT":
      return "Locação"
    case "SEARCH":
      return "Pesquisou"
    case "SEASON_RENTAL":
      return "Temporada"
    case "VISIT":
      return "Visita"
    case "VIVA_REAL":
      return 'Viva Real';
    case "WHATSAPP":
      return "Whatsapp"
    case "ZAP":
      return 'Zap';
    case "PARTNERSHIP":
      return "Parceria";
    case "RD_STATION":
      return "RD Station";
    case "FORM_SITE_TANTUS":
      return "Formulário Site Tantus";
    case "MARKETING":
      return "Marketing";
    case "CLIQUEI_MUDEI":
      return "Cliquei Mudei";
    case "CREDITO_REAL":
      return "Crédito Real";
    case "LEADS_4_SALES":
      return "Leads4Sales";
    case "DEFAULTER":
      return "Inadimplente";
    case "IN_AGREEMENT":
      return "Em acordo";
    case "COTA_1":
      return "1 Cota";
    case "COTA_2":
      return "2s Cotas";
    case "COTA_3":
      return "3s Cotas";
    case "COTA_4":
      return "4s Cotas";
    case "COTA_5":
      return "5s Cotas";
    case "COTA_6":
      return "6+ Cotas";
    case "IS_ACTIVE":
      return "Ativo";
    case "IS_INACTIVE":
      return "Inativo";
    case "IS_30":
      return "30"
    case "IS_60":
      return "60";
    case "IS_90":
      return "90";
    case "IS_PLUS_90":
      return "90+";
    case "RECIBO_1":
      return "1 Recibo";
    case "RECIBO_2":
      return "2 Recibos";
    case "RECIBO_3":
      return "3 Recibos";
    case "RECIBO_4":
      return "4+ Recibos";
    case "LAIS_IA":
      return "Lais Webhook";
    default:
      return tag || ''
  }
};

export const prettifyMoney = (value: number) => {
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  return formatter.format(value); /* R$2.500,00 */
}

export const prettifyMoneyUsd = (value: number, maximumFractionDigits?: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maximumFractionDigits ?? 2,
  });

  return formatter.format(value); /* $2,500.00 */
}

export const prettifyMoneyUsdPrecise = (value: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

  return formatter.format(value); /* $2,500.0000 */
}


export const prettifyUserRouletteEventType = (type: UserRouletteEventType): string => {
  switch (type) {
    case UserRouletteEventType.CHECKIN_SUCCESS:
      return "Sucesso no checkin"
    case UserRouletteEventType.CHECKIN_FAILED:
      return "Falha no checkin"
    case UserRouletteEventType.CHECKOUT_SUCCESS:
      return "Sucesso no checkout"
    case UserRouletteEventType.CHECKOUT_FAILED:
      return "Falha no checkout"
    case UserRouletteEventType.AUTOMATIC_CHECKOUT:
      return "Checkout automático"
    case UserRouletteEventType.CLIENT_SHARED:
      return "Lead compartilhado"
    case UserRouletteEventType.CLIENT_SHARED_AFTER_CHECKIN:
      return "Lead compartilhado no checkin"
    case UserRouletteEventType.CLIENTS_FILTERED_FOR_CHECKIN:
      return "Filtro de leads sem corretor no checkin"

    default:
      return type
  }
}

export const prettifyChatSubFlowName = (chatSubFlowName: ChatSubFlowName | string) => {
  switch (chatSubFlowName) {
    case ChatSubFlowName.ALREADY_CLIENT_CHAT_FLOW: return 'Já sou cliente';
    case ChatSubFlowName.ANGARIATION_CHAT_FLOW: return 'Angariação';
    case ChatSubFlowName.APOLAR_BILLET_CHAT_FLOW: return 'Boleto (Apolar)';
    case ChatSubFlowName.APOLAR_EXTRACT_LIST_CHAT_FLOW: return 'Extrato (Apolar)';
    case ChatSubFlowName.APOLAR_REVENUE_CHAT_FLOW: return 'Imposto de renda (Apolar)';
    case ChatSubFlowName.BG_SECONDARY_MENU_CHAT_FLOW: return 'Menu secundário (BG)';
    case ChatSubFlowName.CORPORATIVE_REAL_ESTATE_QUESTIONS_CHAT_FLOW: return 'Perguntas sobre imóveis (Corporativo)';
    case ChatSubFlowName.CUSTOMER_DATA_CHAT_FLOW: return 'Dados do cliente';
    case ChatSubFlowName.ENDING_CHAT_FLOW: return 'Encerramento';
    case ChatSubFlowName.FINANCIAL_SIMULATION_FLOW: return 'Simulação de financiamento';
    case ChatSubFlowName.FINANCING_QUESTIONS_CHAT_FLOW: return 'Perguntas sobre financiamento (completo)';
    case ChatSubFlowName.FINANCING_QUESTIONS_KEY_ON_CHAT_FLOW: return 'Perguntas sobre financiamento (Key.On)';
    case ChatSubFlowName.FINANCING_QUESTIONS_MINIMAL_CHAT_FLOW: return 'Perguntas sobre financiamento (mínimo)';
    case ChatSubFlowName.FINANCING_QUESTIONS_TOFANO_CHAT_FLOW: return 'Perguntas sobre financiamento (Tofano)';
    case ChatSubFlowName.FUNNEL_QUERY_CHAT_FLOW: return 'Consulta de etapa funil';
    case ChatSubFlowName.FUNNEL_QUERY_CLIENTS_CHAT_FLOW: return 'Consulta de clientes por etapa funil';
    case ChatSubFlowName.GENERIC_BILLET_CHAT_FLOW: return 'Boleto';
    case ChatSubFlowName.GENERIC_CONTRACT_CHAT_FLOW: return 'Contrato';
    case ChatSubFlowName.GENERIC_OWNER_EXTRACT_CHAT_FLOW: return 'Extrato do proprietário';
    case ChatSubFlowName.HUMAN_RESOURCES_FLOW: return 'Recursos humanos';
    case ChatSubFlowName.KUROLE_OCCURRENCE: return 'Ocorrências (Kurole)';
    case ChatSubFlowName.REAL_ESTATE_NO_SEARCH_CHAT_FLOW: return 'Perguntas sobre imóveis';
    case ChatSubFlowName.REAL_ESTATE_QUESTIONS_CHAT_FLOW: return 'Pesquisa de imóveis';
    case ChatSubFlowName.REAL_ESTATE_SHOW_CHAT_FLOW: return 'Exibição de imóveis';
    case ChatSubFlowName.SPEAK_ABOUT_CONDO_CHAT_FLOW: return 'Falar com corretor (condomínio)';
    case ChatSubFlowName.SPEAK_WITH_SALESMAN_CHAT_FLOW: return 'Falar com corretor (completo)';
    case ChatSubFlowName.SPEAK_WITH_SALESMAN_REDUX_CHAT_FLOW: return 'Falar com corretor (mínimo)';
    case ChatSubFlowName.START: return 'Início';
    case ChatSubFlowName.VISIT_QUESTIONS_CHAT_FLOW: return 'Agendamento de visita';
    case ChatSubFlowName.VISIT_QUESTIONS_NO_REAL_ESTATE_CHAT_FLOW: return 'Agendamento de visita Aliança';
    case ChatSubFlowName.BAND_VALE_CUSTOMER_DATA: return 'Cadastro Band Vale';
    case ChatSubFlowName.RESCISSION_EVALUATION_CHAT_FLOW: return 'Pesquisa de rescisão';

    case ChatSubFlowName.AUX_ONE_CHAT_FLOW: return "Auxiliar 1"
    case ChatSubFlowName.AUX_TWO_CHAT_FLOW: return "Auxiliar 2"
    case ChatSubFlowName.AUX_THREE_CHAT_FLOW: return "Auxiliar 3"
    case ChatSubFlowName.AUX_FOUR_CHAT_FLOW: return "Auxiliar 4"
    case ChatSubFlowName.AUX_FIVE_CHAT_FLOW: return "Auxiliar 5"
    case ChatSubFlowName.AUX_SIX_CHAT_FLOW: return "Auxiliar 6"
    case ChatSubFlowName.AUX_SEVEN_CHAT_FLOW: return "Auxiliar 7"
    case ChatSubFlowName.AUX_EIGHT_CHAT_FLOW: return "Auxiliar 8"
    case ChatSubFlowName.AUX_NINE_CHAT_FLOW: return "Auxiliar 9"
    case ChatSubFlowName.AUX_TEN_CHAT_FLOW: return "Auxiliar 10"
    case ChatSubFlowName.AUX_ELEVEN_CHAT_FLOW: return "Auxiliar 11"
    case ChatSubFlowName.AUX_TWELVE_CHAT_FLOW: return "Auxiliar 12"
    case ChatSubFlowName.AUX_THIRTEEN_CHAT_FLOW: return "Auxiliar 13"
    case ChatSubFlowName.AUX_FOURTEEN_CHAT_FLOW: return "Auxiliar 14"
    case ChatSubFlowName.AUX_FIFTEEN_CHAT_FLOW: return "Auxiliar 15"
    case ChatSubFlowName.AUX_SIXTEEN_CHAT_FLOW: return "Auxiliar 16"
    case ChatSubFlowName.AUX_SEVENTEEN_CHAT_FLOW: return "Auxiliar 17"
    case ChatSubFlowName.AUX_EIGHTEEN_CHAT_FLOW: return "Auxiliar 18"
    case ChatSubFlowName.AUX_NINETEEN_CHAT_FLOW: return "Auxiliar 19"
    case ChatSubFlowName.AUX_TWENTY_CHAT_FLOW: return "Auxiliar 20"
    case ChatSubFlowName.AUX_TWENTY_ONE_CHAT_FLOW: return "Auxiliar 21"
    case ChatSubFlowName.AUX_TWENTY_TWO_CHAT_FLOW: return "Auxiliar 22"
    case ChatSubFlowName.AUX_TWENTY_THREE_CHAT_FLOW: return "Auxiliar 23"
    case ChatSubFlowName.AUX_TWENTY_FOUR_CHAT_FLOW: return "Auxiliar 24"
    case ChatSubFlowName.AUX_TWENTY_FIVE_CHAT_FLOW: return "Auxiliar 25"
    case ChatSubFlowName.AUX_TWENTY_SIX_CHAT_FLOW: return "Auxiliar 26"
    case ChatSubFlowName.AUX_TWENTY_SEVEN_CHAT_FLOW: return "Auxiliar 27"
    case ChatSubFlowName.AUX_TWENTY_EIGHT_CHAT_FLOW: return "Auxiliar 28"
    case ChatSubFlowName.AUX_TWENTY_NINE_CHAT_FLOW: return "Auxiliar 29"
    case ChatSubFlowName.AUX_THIRTY_CHAT_FLOW: return "Auxiliar 30"
    case ChatSubFlowName.INPUT_ONE_CHAT_FLOW: return "Input 1"
    case ChatSubFlowName.INPUT_TWO_CHAT_FLOW: return "Input 2"
    case ChatSubFlowName.INPUT_THREE_CHAT_FLOW: return "Input 3"
    case ChatSubFlowName.INPUT_FOUR_CHAT_FLOW: return "Input 4"
    case ChatSubFlowName.INPUT_FIVE_CHAT_FLOW: return "Input 5"
    case ChatSubFlowName.INPUT_SIX_CHAT_FLOW: return "Input 6"
    case ChatSubFlowName.INPUT_SEVEN_CHAT_FLOW: return "Input 7"
    case ChatSubFlowName.INPUT_EIGHT_CHAT_FLOW: return "Input 8"
    case ChatSubFlowName.INPUT_NINE_CHAT_FLOW: return "Input 9"
    case ChatSubFlowName.INPUT_TEN_CHAT_FLOW: return "Input 10"

    default: return ""
  }
}

export const prettifyCrcStepShowEnum = (crc: CrcStepShowEnum) => {
  switch (crc) {
    case CrcStepShowEnum.ALREADY_CLIENT: return 'Fluxo "Já sou cliente"'
    case CrcStepShowEnum.AUX_ONE: return "Auxiliar 1"
    case CrcStepShowEnum.AUX_TWO: return "Auxiliar 2"
    case CrcStepShowEnum.AUX_THREE: return "Auxiliar 3"
    case CrcStepShowEnum.AUX_FOUR: return "Auxiliar 4"
    case CrcStepShowEnum.AUX_FIVE: return "Auxiliar 5"
    case CrcStepShowEnum.AUX_SIX: return "Auxiliar 6"
    case CrcStepShowEnum.AUX_SEVEN: return "Auxiliar 7"
    case CrcStepShowEnum.AUX_EIGHT: return "Auxiliar 8"
    case CrcStepShowEnum.AUX_NINE: return "Auxiliar 9"
    case CrcStepShowEnum.AUX_TEN: return "Auxiliar 10"
    case CrcStepShowEnum.AUX_ELEVEN: return "Auxiliar 11"
    case CrcStepShowEnum.AUX_TWELVE: return "Auxiliar 12"
    case CrcStepShowEnum.AUX_THIRTEEN: return "Auxiliar 13"
    case CrcStepShowEnum.AUX_FOURTEEN: return "Auxiliar 14"
    case CrcStepShowEnum.AUX_FIFTEEN: return "Auxiliar 15"
    case CrcStepShowEnum.AUX_SIXTEEN: return "Auxiliar 16"
    case CrcStepShowEnum.AUX_SEVENTEEN: return "Auxiliar 17"
    case CrcStepShowEnum.AUX_EIGHTEEN: return "Auxiliar 18"
    case CrcStepShowEnum.AUX_NINETEEN: return "Auxiliar 19"
    case CrcStepShowEnum.AUX_TWENTY: return "Auxiliar 20"
    case CrcStepShowEnum.AUX_TWENTY_ONE: return "Auxiliar 21"
    case CrcStepShowEnum.AUX_TWENTY_TWO: return "Auxiliar 22"
    case CrcStepShowEnum.AUX_TWENTY_THREE: return "Auxiliar 23"
    case CrcStepShowEnum.AUX_TWENTY_FOUR: return "Auxiliar 24"
    case CrcStepShowEnum.AUX_TWENTY_FIVE: return "Auxiliar 25"
    case CrcStepShowEnum.AUX_TWENTY_SIX: return "Auxiliar 26"
    case CrcStepShowEnum.AUX_TWENTY_SEVEN: return "Auxiliar 27"
    case CrcStepShowEnum.AUX_TWENTY_EIGHT: return "Auxiliar 28"
    case CrcStepShowEnum.AUX_TWENTY_NINE: return "Auxiliar 29"
    case CrcStepShowEnum.AUX_THIRTY: return "Auxiliar 30"
    case CrcStepShowEnum.INPUT_ONE: return "Input 1"
    case CrcStepShowEnum.INPUT_TWO: return "Input 2"
    case CrcStepShowEnum.INPUT_THREE: return "Input 3"
    case CrcStepShowEnum.INPUT_FOUR: return "Input 4"
    case CrcStepShowEnum.INPUT_FIVE: return "Input 5"
    case CrcStepShowEnum.INPUT_SIX: return "Input 6"
    case CrcStepShowEnum.INPUT_SEVEN: return "Input 7"
    case CrcStepShowEnum.INPUT_EIGHT: return "Input 8"
    case CrcStepShowEnum.INPUT_NINE: return "Input 9"
    case CrcStepShowEnum.INPUT_TEN: return "Input 10"
    case CrcStepShowEnum.REAL_ESTATE: return "Imóveis"
    case CrcStepShowEnum.START: return "Início"
    case CrcStepShowEnum.BAND_VALE_CUSTOMER_DATA: return "Cadastro Band Vale"

    default: return crc;
  }
};

export const prettifyRouletteStatus = (status: RouletteStatus) => {
  switch (status) {
    case RouletteStatus.CHECKED_IN: return 'Checkin feito';
    case RouletteStatus.CHECKIN_DISABLED: return 'Checkin desabilitado';
    case RouletteStatus.DISABLED_AT_AGENCY: return 'Checkin desabilitado na agência';
    case RouletteStatus.NOT_CHECKED_IN: return 'Checkin não realizado';
    case RouletteStatus.NOT_IN_GROUP: return 'Sem grupos disponíveis';
    case RouletteStatus.NO_AGENCY_FOR_USER: return 'Usuário não vinculado a nenhuma agência';

    default: return status;
  }
}

/**
 * Adds zero to the left if needed
 * @param nmb number to standartize
 */
export const twoDigits = (nmb: number) => {
  if (nmb > 9 || nmb < -9) return nmb.toString();
  return ('0' + nmb);
}

export const prettifyExternalChatChannelType = (type: ExternalChatChannelType) => {
  switch (type) {
    case ExternalChatChannelType.CHAT_WIDGET: return 'Chat Widget';
    case ExternalChatChannelType.FACEBOOK_MESSENGER: return 'Facebook Messenger';
    case ExternalChatChannelType.INTERNAL_USER_CHAT: return 'Chat Interno';
    case ExternalChatChannelType.WHATSAPP: return 'Whatsapp Web';
    case ExternalChatChannelType.WHATSAPP_Z_API: return 'Whatsapp Z-API';
    case ExternalChatChannelType.WHATSAPP_GUPSHUP: return 'Whatsapp Gupshup API';
    case ExternalChatChannelType.WHATSAPP_POSITUS: return 'Whatsapp Positus API';

    default: return type;
  }
}

export const formatCep = (cep: string) => {
  if (!cep) return '';

  return cep
    .replace(/[^0-9]/g, '')
    .slice(0, 8)
    .replace(/(\d{5})(\d{3})/, '$1-$2');
};

export const prettifyFunnelStepActionType = (type: FunnelStepActionType): string => {
  switch (type) {
    case FunnelStepActionType.SEND_MESSAGE:
      return "Enviar mensagem";

    default:
      return type
  }
};

export const prettifyBoolean = (value: boolean) => {
  return value ? 'Sim' : 'Não';
}

export const prettifyDashboardPage = (page: DashboardPage) => {
  switch (page) {
    case DashboardPage.AGENCIES_PAGE:
      return 'Agências';
    case DashboardPage.ATTENDANT_EVALUATIONS_PAGE:
      return 'Avaliações do atendente';
    case DashboardPage.BOT_EVALUATIONS_PAGE:
      return 'Avaliações do bot';
    case DashboardPage.CHAT_ONLINE_PAGE:
      return 'Chat Online';
    case DashboardPage.CLIENTS_CHOICES_PAGE:
      return 'Preferências';
    case DashboardPage.CLIENTS_EMAILS_PAGE:
      return 'E-mails';
    case DashboardPage.CLIENTS_ESTATISTICS_PAGE:
      return 'Estatísticas (Clientes)';
    case DashboardPage.CLIENTS_PAGE:
      return 'Clientes';
    case DashboardPage.CLIENTS_SEARCH_PAGE:
      return 'Busca Personalizada';
    case DashboardPage.CLIENTS_SUM_PAGE:
      return 'Somatórios';
    case DashboardPage.CLIENT_GROUP_PAGE:
      return 'Grupo';
    case DashboardPage.DELETED_CLIENTS_PAGE:
      return 'Clientes excluídos';
    case DashboardPage.ERRORS_OCCURRED_PAGE:
      return 'Erros ocorridos';
    case DashboardPage.EXTERNAL_CHAT_CHANNEL_STATS_PAGE:
      return 'Estatísticas Chat Online';
    case DashboardPage.EXTERNAL_CHAT_CHANNEL_GUPSHUP_DAILY_USAGE_PAGE:
      return 'Estatísticas de custos Gupshup';
    case DashboardPage.FAIL_CHAT_PAGE:
      return 'Erros observados';
    case DashboardPage.FUNNEL_STEPS_EDIT_PAGE:
      return 'Funil de processos (edição)';
    case DashboardPage.FUNNEL_STEPS_PAGE:
      return 'Funil de processos';
    case DashboardPage.MESSAGES_TO_SALESMAN_PAGE:
      return 'Mensagens para corretor';
    case DashboardPage.PORTIFOLIO_ANALYSIS_PAGE:
      return 'Análise de carteira';
    case DashboardPage.REAL_ESTATE_AGENCIES_DATABASES_PAGE:
      return 'Bases de dados de imóveis';
    case DashboardPage.REAL_ESTATE_DATA_QUALITY_PAGE:
      return 'Qualidade dos dados';
    case DashboardPage.REAL_ESTATE_RECOMMENDATION_PAGE:
      return 'Recomendação de imóveis';
    case DashboardPage.REAL_ESTATE_SEARCH_PAGE:
      return 'Pesquisa de imóveis';
    case DashboardPage.REAL_ESTATE_STATISTICS_PAGE:
      return 'Estatísticas (Imóveis)';
    case DashboardPage.REAL_TIME_GRAPHS_PAGE:
      return 'Gráficos em tempo real';
    case DashboardPage.REGION_EDIT_PAGE:
      return 'Editar Regiões';
    case DashboardPage.RESCISSION_EVALUATIONS_PAGE:
      return 'Pesquisa de rescisão';
    case DashboardPage.ROULETTE_PAGE:
      return 'Roleta';
    case DashboardPage.SCHEDULE_MESSAGES_PAGE:
      return 'Mensagens agendadas';
    case DashboardPage.SEARCH_CLIENTS_BY_REAL_ESTATE_PAGE:
      return 'Pesquisa de clientes por imóvel';
    case DashboardPage.TEMPLATES_PAGE:
      return 'Templates';
    case DashboardPage.USERS_PAGE:
      return 'Usuários';
    case DashboardPage.USER_CREATE_PAGE:
      return 'Usuários (criação)';
    case DashboardPage.USER_EDIT_PAGE:
      return 'Usuários (edição)';
    case DashboardPage.VISITS_CALENDAR_PAGE:
      return 'Solicitações de visita';
    case DashboardPage.CUSTOM_REACTIVE_CHAT_QUESTION_PAGE:
      return 'Personalização do chat';
    case DashboardPage.MESSAGES_REPORT_PAGE:
      return 'Relatório de mensagens do ChatOnline'
    case DashboardPage.MERGE_CLIENTS_PAGE:
      return 'Unificar clientes';
    case DashboardPage.TAGS_PAGE:
      return 'Tags';
    default:
      return page;
  }
}

export const prettifyClientsImporterType = (type: ClientsImporterType) => {
  switch (type) {
    case ClientsImporterType.APOLAR:
      return "Apolar Inadimplentes";
    case ClientsImporterType.VOUCH:
      return "Vouch";

    default:
      return type
  }
}

export const prettifyGupshupTemplateCategory = (category: GupshupTemplateCategory) => {
  switch (category) {
    case GupshupTemplateCategory.AUTHENTICATION:
      return "Autenticação";
    case GupshupTemplateCategory.MARKETING:
      return "Marketing";
    case GupshupTemplateCategory.UTILITY:
      return "Utilitário";

    default:
      return category
  }
}

export const prettifyGupshupTemplateType = (type: GupshupTemplateType) => {
  switch (type) {
    case GupshupTemplateType.TEXT:
      return 'Texto';
    case GupshupTemplateType.IMAGE:
      return 'Imagem';
    case GupshupTemplateType.LOCATION:
      return 'Localização';
    case GupshupTemplateType.VIDEO:
      return 'Vídeo';
    case GupshupTemplateType.DOCUMENT:
      return 'Documento';
    case GupshupTemplateType.PRODUCT:
      return 'Produto';
    case GupshupTemplateType.CATALOG:
      return 'Catálogo';

    default:
      return type
  }
}

export const prettifyGupshupTemplateStatus = (status: GupshupTemplateStatus) => {
  switch (status) {
    case GupshupTemplateStatus.PENDING:
      return 'Pendente';
    case GupshupTemplateStatus.APPROVED:
      return 'Aprovado';
    case GupshupTemplateStatus.REJECTED:
      return 'Rejeitado';
    case GupshupTemplateStatus.SUBMITTED:
      return 'Enviado';
    case GupshupTemplateStatus.DEACTIVATED:
      return 'Desativado';
    case GupshupTemplateStatus.PAUSED:
      return 'Pausado';
    case GupshupTemplateStatus.FAILED:
      return 'Falhou';
    case GupshupTemplateStatus.ARCHIVED:
      return 'Arquivado';

    default:
      return status;
  }
}

export const prettifyObservationSendingMethod = (sendingMethod: ObservationSendingMethod) => {
  if (!sendingMethod) return null

  switch (sendingMethod) {
    case ObservationSendingMethod.WHATSAPP_FIREFLY: return "Whatsapp Firefly"
    case ObservationSendingMethod.WHATSAPP_LINK: return "Link Whatsapp"
    case ObservationSendingMethod.EMAIL: return "Email"
    default: return sendingMethod
  }
}

export const prettifyEmailTemplateType = (type: EmailTemplateType) => {
  switch (type) {
    case EmailTemplateType.CLIENT_OBSERVATION: return "Observação do Cliente"
    case EmailTemplateType.CLIENT_OBSERVATION_NOTIFICATION: return "Notificação de Observação do Cliente"
    default: return type
  }
}
